import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Download, 
  Share2, 
  Play, 
  Pause, 
  Clock, 
  Phone,
  User,
  MessageSquare,
  BarChart3,
  PieChart,
  Activity,
  CheckCircle,
  AlertCircle,
  Star,
  RefreshCw,
  PhoneCall,
  PhoneMissed,
  PhoneIncoming,
  PhoneOutgoing,
  Timer,
  Target,
  Award
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

interface CallAuditData {
  candidate: {
    id: string;
    name: string;
    email: string;
    phone: string;
    role: string;
    avatar?: string;
  };
  recruiter: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  calls: {
    id: string;
    type: 'incoming' | 'outgoing' | 'missed';
    duration: number;
    timestamp: string;
    status: 'completed' | 'missed' | 'cancelled';
    quality: 'excellent' | 'good' | 'fair' | 'poor';
    recordingUrl?: string;
    transcript?: string;
    notes?: string;
    sentiment: 'positive' | 'neutral' | 'negative';
    topics: string[];
    keywords: string[];
  }[];
  metrics: {
    totalCalls: number;
    totalDuration: number;
    avgDuration: number;
    completionRate: number;
    responseTime: number;
    engagementScore: number;
    satisfactionScore: number;
    followUpRequired: number;
  };
  analysis: {
    overallSentiment: 'positive' | 'neutral' | 'negative';
    keyTopics: { topic: string; frequency: number; sentiment: string }[];
    conversationFlow: { timestamp: number; speaker: string; content: string; sentiment: string }[];
    recommendations: string[];
    redFlags: string[];
    strengths: string[];
  };
  timeline: {
    date: string;
    event: string;
    type: 'call' | 'email' | 'meeting' | 'note';
    status: 'completed' | 'pending' | 'cancelled';
    duration?: number;
    notes?: string;
  }[];
}

const CallAuditReportPage: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [auditData, setAuditData] = useState<CallAuditData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'calls' | 'analysis' | 'timeline'>('overview');
  const [isPlaying, setIsPlaying] = useState(false);

  // Generate hypothetical data
  const generateHypotheticalData = (): CallAuditData => ({
    candidate: {
      id: candidateId || 'cand_123',
      name: 'Michael Chen',
      email: 'michael.chen@email.com',
      phone: '+1 (555) 987-6543',
      role: 'Senior Product Manager',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
    },
    recruiter: {
      id: 'rec_456',
      name: 'Sarah Williams',
      email: 'sarah.williams@company.com',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face'
    },
    calls: [
      {
        id: 'call_001',
        type: 'outgoing',
        duration: 1847, // 30:47
        timestamp: '2024-01-15T14:30:00Z',
        status: 'completed',
        quality: 'excellent',
        recordingUrl: '/recordings/call_001.mp3',
        transcript: 'Initial screening call with Michael Chen. Discussed his background in product management and interest in our company...',
        notes: 'Very engaged, asked thoughtful questions about the role and company culture.',
        sentiment: 'positive',
        topics: ['background', 'experience', 'company culture', 'role expectations'],
        keywords: ['product management', 'agile', 'stakeholder', 'roadmap', 'user experience']
      },
      {
        id: 'call_002',
        type: 'incoming',
        duration: 1205, // 20:05
        timestamp: '2024-01-16T10:15:00Z',
        status: 'completed',
        quality: 'good',
        recordingUrl: '/recordings/call_002.mp3',
        transcript: 'Follow-up call to discuss technical requirements and team structure...',
        notes: 'Had some concerns about remote work policy, but overall positive.',
        sentiment: 'neutral',
        topics: ['technical requirements', 'team structure', 'remote work', 'compensation'],
        keywords: ['remote', 'hybrid', 'team', 'technical', 'requirements']
      },
      {
        id: 'call_003',
        type: 'outgoing',
        duration: 2100, // 35:00
        timestamp: '2024-01-18T16:45:00Z',
        status: 'completed',
        quality: 'excellent',
        recordingUrl: '/recordings/call_003.mp3',
        transcript: 'Final interview preparation call. Covered interview process and next steps...',
        notes: 'Very prepared and enthusiastic. Strong candidate.',
        sentiment: 'positive',
        topics: ['interview process', 'next steps', 'preparation', 'questions'],
        keywords: ['interview', 'preparation', 'process', 'next steps', 'enthusiastic']
      },
      {
        id: 'call_004',
        type: 'missed',
        duration: 0,
        timestamp: '2024-01-20T09:00:00Z',
        status: 'missed',
        quality: 'poor',
        notes: 'Candidate missed scheduled call. Left voicemail.',
        sentiment: 'neutral',
        topics: ['scheduling', 'availability'],
        keywords: ['missed', 'voicemail', 'reschedule']
      }
    ],
    metrics: {
      totalCalls: 4,
      totalDuration: 5152, // 1:25:52
      avgDuration: 1288, // 21:28
      completionRate: 75,
      responseTime: 120, // 2 minutes
      engagementScore: 88,
      satisfactionScore: 85,
      followUpRequired: 1
    },
    analysis: {
      overallSentiment: 'positive',
      keyTopics: [
        { topic: 'Company Culture', frequency: 12, sentiment: 'positive' },
        { topic: 'Role Expectations', frequency: 10, sentiment: 'positive' },
        { topic: 'Technical Requirements', frequency: 8, sentiment: 'neutral' },
        { topic: 'Remote Work', frequency: 6, sentiment: 'neutral' },
        { topic: 'Compensation', frequency: 5, sentiment: 'positive' }
      ],
      conversationFlow: [
        { timestamp: 0, speaker: 'Recruiter', content: 'Hi Michael, thanks for taking the time to speak with me today.', sentiment: 'positive' },
        { timestamp: 30, speaker: 'Candidate', content: 'Hi Sarah, thank you for reaching out. I\'m excited to learn more about this opportunity.', sentiment: 'positive' },
        { timestamp: 60, speaker: 'Recruiter', content: 'Great! Can you tell me about your background in product management?', sentiment: 'neutral' },
        { timestamp: 120, speaker: 'Candidate', content: 'I\'ve been in product management for about 6 years, primarily in SaaS companies...', sentiment: 'positive' },
        { timestamp: 300, speaker: 'Recruiter', content: 'That\'s impressive. What draws you to our company specifically?', sentiment: 'positive' },
        { timestamp: 360, speaker: 'Candidate', content: 'I\'m really excited about your mission and the impact you\'re making in the industry.', sentiment: 'positive' }
      ],
      recommendations: [
        'Schedule technical interview with engineering team',
        'Conduct cultural fit assessment',
        'Prepare salary negotiation strategy',
        'Arrange follow-up call to address remote work concerns',
        'Complete reference checks'
      ],
      redFlags: [
        'Missed one scheduled call without prior notice',
        'Some hesitation about remote work policy'
      ],
      strengths: [
        'Excellent communication skills',
        'Strong technical background',
        'Enthusiastic about the role',
        'Asks thoughtful questions',
        'Good cultural fit'
      ]
    },
    timeline: [
      {
        date: '2024-01-15T14:30:00Z',
        event: 'Initial screening call',
        type: 'call',
        status: 'completed',
        duration: 1847,
        notes: 'Very engaged, asked thoughtful questions'
      },
      {
        date: '2024-01-15T15:00:00Z',
        event: 'Follow-up email sent',
        type: 'email',
        status: 'completed',
        notes: 'Sent company information and next steps'
      },
      {
        date: '2024-01-16T10:15:00Z',
        event: 'Follow-up call',
        type: 'call',
        status: 'completed',
        duration: 1205,
        notes: 'Discussed technical requirements'
      },
      {
        date: '2024-01-18T16:45:00Z',
        event: 'Interview prep call',
        type: 'call',
        status: 'completed',
        duration: 2100,
        notes: 'Very prepared and enthusiastic'
      },
      {
        date: '2024-01-20T09:00:00Z',
        event: 'Scheduled call - Missed',
        type: 'call',
        status: 'cancelled',
        notes: 'Candidate missed call, left voicemail'
      }
    ]
  });

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setAuditData(generateHypotheticalData());
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [candidateId]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-500 bg-green-50 dark:bg-green-900/20';
      case 'neutral': return 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
      case 'negative': return 'text-red-500 bg-red-50 dark:bg-red-900/20';
      default: return 'text-text-tertiary bg-background-50 dark:bg-background-dark-200/20';
    }
  };

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent': return 'text-green-500';
      case 'good': return 'text-blue-500';
      case 'fair': return 'text-yellow-500';
      case 'poor': return 'text-red-500';
      default: return 'text-text-tertiary';
    }
  };

  const getCallTypeIcon = (type: string) => {
    switch (type) {
      case 'incoming': return PhoneIncoming;
      case 'outgoing': return PhoneOutgoing;
      case 'missed': return PhoneMissed;
      default: return PhoneCall;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-50 via-white to-background-100 dark:from-background-dark-200 dark:via-surface-dark-100 dark:to-background-dark-200 flex items-center justify-center">
        <div className="text-center space-y-4">
          <LoadingSpinner size="xl" variant="ai" className="mx-auto" />
          <p className="text-lg text-text-secondary dark:text-text-secondary">Analyzing Call Data...</p>
        </div>
      </div>
    );
  }

  if (!auditData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-50 via-white to-background-100 dark:from-background-dark-200 dark:via-surface-dark-100 dark:to-background-dark-200 flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
          <p className="text-lg text-text-secondary dark:text-text-secondary">Failed to load audit data</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-background-50 via-white to-background-100 dark:from-background-dark-200 dark:via-surface-dark-100 dark:to-background-dark-200">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="hover:bg-white/20 transition-all duration-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Call Audit Report
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Comprehensive call analysis and communication insights
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" className="glass-card hover:glass-card-hover">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button size="sm" className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>

          {/* Candidate & Recruiter Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Candidate Card */}
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6 hover:shadow-3xl transition-all duration-500">
              <div className="flex items-start space-x-4">
                <div className="relative">
                  <img
                    src={auditData.candidate.avatar}
                    alt={auditData.candidate.name}
                    className="w-16 h-16 rounded-full object-cover ring-4 ring-white/20"
                  />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center">
                    <Phone className="w-3 h-3 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-text-primary dark:text-text-primary mb-2">
                    {auditData.candidate.name}
                  </h3>
                  <p className="text-text-secondary dark:text-text-secondary mb-3">{auditData.candidate.role}</p>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {auditData.candidate.email}
                    </div>
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <Phone className="w-4 h-4 mr-2" />
                      {auditData.candidate.phone}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Recruiter Card */}
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6 hover:shadow-3xl transition-all duration-500">
              <div className="flex items-start space-x-4">
                <div className="relative">
                  <img
                    src={auditData.recruiter.avatar}
                    alt={auditData.recruiter.name}
                    className="w-16 h-16 rounded-full object-cover ring-4 ring-white/20"
                  />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <User className="w-3 h-3 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-text-primary dark:text-text-primary mb-2">
                    {auditData.recruiter.name}
                  </h3>
                  <p className="text-text-secondary dark:text-text-secondary mb-3">Senior Recruiter</p>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {auditData.recruiter.email}
                    </div>
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <Award className="w-4 h-4 mr-2" />
                      Lead Recruiter
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Calls', value: auditData.metrics.totalCalls, icon: Phone, color: 'blue' },
              { label: 'Total Duration', value: formatDuration(auditData.metrics.totalDuration), icon: Clock, color: 'green' },
              { label: 'Completion Rate', value: `${auditData.metrics.completionRate}%`, icon: CheckCircle, color: 'purple' },
              { label: 'Engagement', value: `${auditData.metrics.engagementScore}%`, icon: Star, color: 'orange' }
            ].map((metric, _index) => (
              <Card key={metric.label} className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-4 hover:shadow-3xl transition-all duration-500">
                <div className="text-center">
                  <metric.icon className={`w-8 h-8 mx-auto mb-2 text-${metric.color}-500`} />
                  <div className="text-2xl font-bold text-text-primary dark:text-text-primary">{metric.value}</div>
                  <div className="text-sm text-text-secondary dark:text-text-secondary">{metric.label}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-white/10 dark:bg-gray-800/10 rounded-xl p-1 backdrop-blur-sm">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'calls', label: 'Calls', icon: Phone },
              { id: 'analysis', label: 'Analysis', icon: PieChart },
              { id: 'timeline', label: 'Timeline', icon: Activity }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-surface-dark-100 shadow-lg text-blue-600 dark:text-blue-400'
                    : 'text-text-secondary dark:text-text-secondary hover:text-text-primary dark:hover:text-text-primary hover:bg-white/5'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Sentiment Analysis */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <PieChart className="w-6 h-6 mr-3 text-green-500" />
                  Sentiment Analysis
                </h3>
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-semibold ${getSentimentColor(auditData.analysis.overallSentiment)}`}>
                    {auditData.analysis.overallSentiment.charAt(0).toUpperCase() + auditData.analysis.overallSentiment.slice(1)} Overall
                  </div>
                </div>
                <div className="space-y-3">
                  {auditData.analysis.keyTopics.map((topic, _index) => (
                    <div key={topic.topic} className="flex items-center justify-between p-3 rounded-lg bg-white/5 dark:bg-gray-800/5">
                      <span className="font-medium text-text-secondary dark:text-text-secondary">{topic.topic}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 bg-background-200 dark:bg-background-dark-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full transition-all duration-1000"
                            style={{ width: `${(topic.frequency / 12) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-text-secondary dark:text-text-secondary">{topic.frequency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Call Quality Metrics */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Activity className="w-6 h-6 mr-3 text-blue-500" />
                  Call Quality Metrics
                </h3>
                <div className="space-y-4">
                  {[
                    { label: 'Average Duration', value: formatDuration(auditData.metrics.avgDuration), icon: Clock },
                    { label: 'Response Time', value: `${auditData.metrics.responseTime} min`, icon: Timer },
                    { label: 'Satisfaction Score', value: `${auditData.metrics.satisfactionScore}%`, icon: Star },
                    { label: 'Follow-ups Required', value: auditData.metrics.followUpRequired, icon: RefreshCw }
                  ].map((metric, _index) => (
                    <div key={metric.label} className="flex items-center justify-between p-3 rounded-lg bg-white/5 dark:bg-gray-800/5 hover:bg-white/10 dark:hover:bg-gray-800/10 transition-all duration-300">
                      <div className="flex items-center space-x-3">
                        <metric.icon className="w-5 h-5 text-blue-500" />
                        <span className="font-medium text-text-secondary dark:text-text-secondary">{metric.label}</span>
                      </div>
                      <span className="text-lg font-bold text-text-primary dark:text-text-primary">{metric.value}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'calls' && (
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <Phone className="w-6 h-6 mr-3 text-blue-500" />
                Call History
              </h3>
              <div className="space-y-4">
                  {auditData.calls.map((call) => {
                  const CallIcon = getCallTypeIcon(call.type);
                  return (
                    <div key={call.id} className="p-4 rounded-xl bg-white/5 dark:bg-gray-800/5 hover:bg-white/10 dark:hover:bg-gray-800/10 transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <CallIcon className={`w-6 h-6 ${
                            call.type === 'incoming' ? 'text-green-500' :
                            call.type === 'outgoing' ? 'text-blue-500' : 'text-red-500'
                          }`} />
                          <div>
                            <h4 className="font-semibold text-text-primary dark:text-text-primary">
                              {call.type.charAt(0).toUpperCase() + call.type.slice(1)} Call
                            </h4>
                            <p className="text-sm text-text-secondary dark:text-text-secondary">
                              {new Date(call.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getSentimentColor(call.sentiment)}`}>
                            {call.sentiment}
                          </div>
                          <div className={`text-sm font-medium ${getQualityColor(call.quality)}`}>
                            {call.quality}
                          </div>
                          {call.duration > 0 && (
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {formatDuration(call.duration)}
                            </div>
                          )}
                        </div>
                      </div>
                      {call.notes && (
                        <p className="text-text-secondary dark:text-text-secondary mb-3">{call.notes}</p>
                      )}
                      {call.recordingUrl && (
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setIsPlaying(!isPlaying)}
                            className="glass-card hover:glass-card-hover"
                          >
                            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </Button>
                          <span className="text-sm text-text-secondary dark:text-text-secondary">Recording available</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {activeTab === 'analysis' && (
            <div className="space-y-8">
              {/* Strengths & Red Flags */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <CheckCircle className="w-6 h-6 mr-3 text-green-500" />
                    Strengths
                  </h3>
                  <div className="space-y-3">
                    {auditData.analysis.strengths.map((strength, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-green-50/50 dark:bg-green-900/20 border border-green-200/50 dark:border-green-700/50">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-text-secondary dark:text-text-secondary">{strength}</span>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <AlertCircle className="w-6 h-6 mr-3 text-yellow-500" />
                    Red Flags
                  </h3>
                  <div className="space-y-3">
                    {auditData.analysis.redFlags.map((flag, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-yellow-50/50 dark:bg-yellow-900/20 border border-yellow-200/50 dark:border-yellow-700/50">
                        <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span className="text-text-secondary dark:text-text-secondary">{flag}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Recommendations */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Target className="w-6 h-6 mr-3 text-purple-500" />
                  Recommendations
                </h3>
                <div className="space-y-3">
                  {auditData.analysis.recommendations.map((recommendation, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-purple-50/50 dark:bg-purple-900/20 border border-purple-200/50 dark:border-purple-700/50">
                      <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">{recommendation}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'timeline' && (
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <Activity className="w-6 h-6 mr-3 text-blue-500" />
                Interaction Timeline
              </h3>
              <div className="space-y-4">
                {auditData.timeline.map((event, _index) => (
                  <div key={_index} className="flex items-start space-x-4 p-4 rounded-xl bg-white/5 dark:bg-gray-800/5 hover:bg-white/10 dark:hover:bg-gray-800/10 transition-all duration-300">
                    <div className={`w-3 h-3 rounded-full mt-2 ${
                      event.status === 'completed' ? 'bg-green-500' :
                      event.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{event.event}</h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-text-tertiary dark:text-text-tertiary">
                            {new Date(event.date).toLocaleDateString()}
                          </span>
                          {event.duration && (
                            <span className="text-sm text-text-tertiary dark:text-text-tertiary">
                              {formatDuration(event.duration)}
                            </span>
                          )}
                        </div>
                      </div>
                      {event.notes && (
                        <p className="text-text-secondary dark:text-text-secondary mt-1">{event.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallAuditReportPage;
